import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Search, Trash2, Plus, RefreshCw, Eye, X, ExternalLink } from 'lucide-react'
import { useOrg } from '../context/OrgContext'
import { getMonitoredAccounts, addMonitoredAccount, removeMonitoredAccount, scanMonitoredAccount, getAccountBreaches } from '../api/endpoints'
import LoadingSpinner from '../components/LoadingSpinner'
import Button from '../components/Button'
import Input from '../components/Input'
import RiskScoreBadge from '../components/RiskScoreBadge'
import Table, { Column } from '../components/Table'
import { MonitoredAccount } from '../api/types'
import { format } from 'date-fns'
import { getBreachResetLink, getBreachTypeLabel } from '../utils/breachLinks'

const MonitoredAccounts: React.FC = () => {
  const { currentOrg } = useOrg()
  const queryClient = useQueryClient()
  const [newEmail, setNewEmail] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [error, setError] = useState('')
  const [selectedAccount, setSelectedAccount] = useState<MonitoredAccount | null>(null)
  const [showBreachModal, setShowBreachModal] = useState(false)

  const { data: accounts, isLoading } = useQuery({
    queryKey: ['monitored-accounts', currentOrg?.id],
    queryFn: () => getMonitoredAccounts(currentOrg!.id),
    enabled: !!currentOrg,
  })

  const addMutation = useMutation({
    mutationFn: (email: string) =>
      addMonitoredAccount({ email, org_id: currentOrg!.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['monitored-accounts'] })
      setNewEmail('')
      setError('')
    },
    onError: (error: Error) => {
      setError(error.message)
    },
  })

  const removeMutation = useMutation({
    mutationFn: (email: string) => removeMonitoredAccount(email, currentOrg!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['monitored-accounts'] })
    },
  })

  const scanMutation = useMutation({
    mutationFn: (email: string) => scanMonitoredAccount(email, currentOrg!.id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['monitored-accounts'] })
      // Could show a toast notification here with scan results
      console.log('Scan completed:', data)
    },
  })

  const { data: breachDetails } = useQuery({
    queryKey: ['account-breaches', selectedAccount?.id],
    queryFn: () => getAccountBreaches(selectedAccount!.id, currentOrg!.id),
    enabled: !!selectedAccount && !!currentOrg,
  })

  const handleAddAccount = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newEmail.trim()) {
      setError('Email is required')
      return
    }
    addMutation.mutate(newEmail)
  }

  const filteredAccounts = accounts?.filter((account) =>
    account.email?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const columns: Column<MonitoredAccount>[] = [
    {
      key: 'email',
      header: 'Email',
      render: (account) => (
        <span className="font-mono text-sm">
          {account.email}
        </span>
      ),
    },
    {
      key: 'risk_score',
      header: 'Risk Score',
      render: (account) => <RiskScoreBadge score={account.risk_score || 0} />,
      sortable: true,
    },
    {
      key: 'breach_count',
      header: 'Breach Count',
      render: (account) => (
        <span className="text-text-primary">{account.breach_count || 0}</span>
      ),
      sortable: true,
    },
    {
      key: 'last_checked',
      header: 'Last Checked',
      render: (account) => (
        <span className="text-text-muted text-sm">
          {account.last_checked_at
            ? format(new Date(account.last_checked_at), 'MMM d, yyyy HH:mm')
            : 'Never'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (account) => (
        <div className="flex gap-2">
          <Button
            variant="secondary"
            icon={<Eye className="w-4 h-4" />}
            onClick={() => {
              setSelectedAccount(account)
              setShowBreachModal(true)
            }}
            disabled={!account.breach_count || account.breach_count === 0}
          >
            View
          </Button>
          <Button
            variant="secondary"
            icon={<RefreshCw className="w-4 h-4" />}
            onClick={() => scanMutation.mutate(account.email)}
            isLoading={scanMutation.isPending}
          >
            Scan
          </Button>
          <Button
            variant="danger"
            icon={<Trash2 className="w-4 h-4" />}
            onClick={() => removeMutation.mutate(account.email)}
            isLoading={removeMutation.isPending}
          >
            Remove
          </Button>
        </div>
      ),
    },
  ]

  if (!currentOrg || isLoading) {
    return <LoadingSpinner fullPage message="Loading monitored accounts..." />
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Monitored Accounts</h1>
        <p className="text-text-muted mt-1">Manage email addresses monitored for breaches</p>
      </div>

      {/* Add Account Form */}
      <div className="bg-background-secondary border border-zinc-700 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-text-primary mb-4">Add New Account</h2>
        <form onSubmit={handleAddAccount} className="flex gap-4">
          <div className="flex-1">
            <Input
              type="email"
              placeholder="email@example.com"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              error={error}
            />
          </div>
          <Button
            type="submit"
            variant="primary"
            icon={<Plus className="w-4 h-4" />}
            isLoading={addMutation.isPending}
          >
            Add Account
          </Button>
        </form>
      </div>

      {/* Search */}
      <div className="bg-background-secondary border border-zinc-700 rounded-lg p-6">
        <div className="flex items-center gap-3">
          <Search className="w-5 h-5 text-text-muted" />
          <Input
            type="text"
            placeholder="Search by email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1"
          />
        </div>
      </div>

      {/* Accounts Table */}
      <Table
        columns={columns}
        data={filteredAccounts || []}
        keyExtractor={(account) => account.id}
        emptyMessage="No monitored accounts found"
      />

      {/* Breach Details Modal */}
      {showBreachModal && selectedAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-background-secondary border border-zinc-700 rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-text-primary">
                Breaches for {selectedAccount.email}
              </h2>
              <button
                onClick={() => {
                  setShowBreachModal(false)
                  setSelectedAccount(null)
                }}
                className="text-text-muted hover:text-text-primary"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              {breachDetails ? (
                <div className="space-y-3">
                  <p className="text-text-muted mb-4">
                    Found in {breachDetails.breach_count} breach{breachDetails.breach_count !== 1 ? 'es' : ''}
                  </p>
                  {breachDetails.breaches.map((breach) => {
                    const resetLink = getBreachResetLink(breach.breach_name)
                    const breachLabel = getBreachTypeLabel(breach.breach_name)

                    return (
                      <div
                        key={breach.id}
                        className="bg-background-primary border border-zinc-700 rounded p-4"
                      >
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-1">
                            <h3 className="font-semibold text-text-primary">
                              {breach.breach_name || 'Unknown Breach'}
                            </h3>
                            <p className="text-sm text-text-muted mt-1">
                              Source: {breach.source.toUpperCase()}
                            </p>
                            <p className="text-sm text-text-muted">
                              Detected: {format(new Date(breach.detected_at), 'MMM d, yyyy HH:mm')}
                            </p>

                            {breachLabel && (
                              <p className="text-xs text-text-muted italic mt-1">
                                {breachLabel}
                              </p>
                            )}

                            {resetLink && (
                              <a
                                href={resetLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 mt-2 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                              >
                                <ExternalLink className="w-4 h-4" />
                                Change Password
                              </a>
                            )}
                          </div>
                          <RiskScoreBadge score={breach.risk_score} />
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <LoadingSpinner message="Loading breach details..." />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MonitoredAccounts
